/**
 * Shared OAuth connect/disconnect helpers for communication integrations.
 */

import { getCurrentSession } from "@/lib/supabase";

export type CommunicationProvider = "ringcentral" | "zoom" | "gohighlevel";

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getCurrentSession();
  if (!session) {
    throw new Error("Not authenticated. Please log in first.");
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
  };
}

function isNetworkFetchError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = error.message.toLowerCase();
  return msg.includes("failed to fetch") || msg.includes("network") || msg.includes("load failed");
}

export async function startCommunicationOAuth(provider: CommunicationProvider): Promise<void> {
  let response: Response;
  try {
    const headers = await getAuthHeaders();
    response = await fetch(`/api/${provider}/oauth/authorize`, { headers });
  } catch (error: unknown) {
    if (isNetworkFetchError(error)) {
      throw new Error("API server not running. Start the app with `npm run dev`.");
    }
    throw error;
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    const message = (err as { error?: string }).error || `Failed to initiate ${provider} OAuth`;
    throw new Error(message);
  }

  const { authUrl } = (await response.json()) as { authUrl?: string };
  if (!authUrl || typeof authUrl !== "string") {
    throw new Error("Invalid OAuth response");
  }
  window.location.href = authUrl;
}

export async function getCommunicationConnectionStatus(
  provider: CommunicationProvider
): Promise<{ connected: boolean; email?: string; name?: string; avatarUrl?: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/${provider}/oauth/status`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error((err as { error?: string }).error || "Failed to check connection status");
  }
  return response.json();
}

export async function disconnectCommunication(provider: CommunicationProvider): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/${provider}/oauth/disconnect`, {
    method: "DELETE",
    headers,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error((err as { error?: string }).error || "Failed to disconnect");
  }
}
